// src/pages/worksheets/WorksheetsPage.tsx
import { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../../firebase";
import {
    FileText,
    Upload,
    Trash2,
    Edit,
    Download,
    Plus,
    Sparkles,
    Check,
    AlertCircle,
    FileUp,
    FolderOpen,
    Settings,
    Info,
    ExternalLink,
    LayoutTemplate,
    Brain,
    Zap,
    Target,
    Clock,
    Users,
    BookOpen,
    FileType,
    RefreshCw,
    Bug
} from "lucide-react";
import { Link } from "react-router-dom";

interface TemplateInfo {
    name: string;
    placeholderCount: number;
    hasTasksPlaceholder: boolean;
    hasTitlePlaceholder: boolean;
    hasDatePlaceholder: boolean;
    sections: string[];
    structure: {
        hasQuestions: boolean;
        hasAnswers: boolean;
        hasExercises: boolean;
        hasFillIn: boolean;
        isWorksheet: boolean;
        isTest: boolean;
        sections: Array<{
            text: string;
            type: string;
            suggestedContent: string;
        }>;
    };
}

interface Template {
    id: string;
    fileName: string;
    templateInfo: TemplateInfo;
    createdAt?: string;
}

export default function WorksheetsPage() {
    const { user } = useAuth();
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState<string>("");
    const [success, setSuccess] = useState<string>("");
    const [debugInfo, setDebugInfo] = useState<any>(null);
    const [showDebug, setShowDebug] = useState(false);

    const loadTemplates = async () => {
        if (!user) return;

        setLoading(true);
        setError("");

        try {
            const getTemplates = httpsCallable(getFunctions(app), "getUserTemplates");
            const result: any = await getTemplates({ userId: user.uid });

            console.log("📋 API Response:", result.data);

            // WICHTIG: Prüfe ob templates Array existiert, nicht ob es einen error gibt
            if (result.data && Array.isArray(result.data.templates)) {
                setTemplates(result.data.templates);
                if (result.data.error) {
                    console.warn("⚠️ Templates gefunden, aber mit Warnung:", result.data.error);
                }
            } else {
                // Wenn keine Templates gefunden wurden, setze leeres Array
                setTemplates([]);
                if (result.data?.error) {
                    setError(result.data.error);
                } else {
                    setError("Keine Vorlagen gefunden");
                }
            }
        } catch (err: any) {
            console.error("❌ Fehler beim Laden der Vorlagen:", err);
            setError("Vorlagen konnten nicht geladen werden.");
            setTemplates([]); // WICHTIG: Auch bei Fehler leeres Array setzen
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) {
            loadTemplates();
        }
    }, [user]);

    // Datei hochladen
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Prüfe Dateityp
        if (!file.name.endsWith('.docx')) {
            setError("Nur .docx Dateien sind erlaubt.");
            return;
        }

        // Prüfe Größe (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError("Datei zu groß (max. 5MB).");
            return;
        }

        setSelectedFile(file);
        setError("");
    };

    const handleUpload = async () => {
        if (!selectedFile || !user) return;

        setUploading(true);
        setUploadProgress(0);
        setError("");

        try {
            // Konvertiere Datei zu base64
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const base64 = e.target?.result as string;
                    const fileData = base64.split(',')[1];

                    const uploadFunction = httpsCallable(getFunctions(app), "uploadTemplate");

                    // Simuliere Fortschritt
                    const progressInterval = setInterval(() => {
                        setUploadProgress(prev => Math.min(prev + 10, 90));
                    }, 200);

                    const result: any = await uploadFunction({
                        fileName: selectedFile.name,
                        fileData,
                        userId: user.uid,
                    });

                    clearInterval(progressInterval);
                    setUploadProgress(100);

                    if (result.data?.error) {
                        setError(result.data.error);
                    } else {
                        setSuccess(`Vorlage "${selectedFile.name}" erfolgreich hochgeladen!`);
                        setSelectedFile(null);
                        setShowUploadModal(false);

                        // Warte kurz bevor neu geladen wird
                        setTimeout(() => {
                            loadTemplates();
                        }, 1000);
                    }
                } catch (err: any) {
                    console.error("Upload-Fehler:", err);
                    setError(`Upload fehlgeschlagen: ${err.message}`);
                } finally {
                    setUploading(false);
                    setUploadProgress(0);
                }
            };

            reader.onerror = () => {
                setError("Datei konnte nicht gelesen werden.");
                setUploading(false);
            };

            reader.readAsDataURL(selectedFile);
        } catch (err: any) {
            console.error("Fehler beim Upload:", err);
            setError(`Upload fehlgeschlagen: ${err.message}`);
            setUploading(false);
        }
    };

    // Vorlage löschen
    const handleDeleteTemplate = async (templateId: string, fileName: string) => {
        if (!user || !window.confirm(`Vorlage "${fileName}" wirklich löschen?`)) return;

        try {
            const deleteFunction = httpsCallable(getFunctions(app), "deleteTemplate");
            const result: any = await deleteFunction({
                templateId,
                userId: user.uid,
            });

            if (result.data?.error) {
                setError(result.data.error);
            } else {
                setSuccess("Vorlage erfolgreich gelöscht!");
                loadTemplates();
            }
        } catch (err: any) {
            console.error("Lösch-Fehler:", err);
            setError("Vorlage konnte nicht gelöscht werden.");
        }
    };

    // Beispiel-Vorlage herunterladen
    const handleDownloadSample = async () => {
        try {
            const sampleFunction = httpsCallable(getFunctions(app), "getSampleTemplate");
            const result: any = await sampleFunction({});

            if (result.data?.file) {
                const blob = b64toBlob(
                    result.data.file,
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                );
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "Beispiel-Vorlage.docx";
                a.click();
                URL.revokeObjectURL(url);

                setSuccess("Beispiel-Vorlage heruntergeladen!");
                setTimeout(() => setSuccess(""), 3000);
            } else if (result.data?.error) {
                setError(result.data.error);
            }
        } catch (err: any) {
            console.error("Fehler beim Herunterladen:", err);
            setError("Beispiel-Vorlage konnte nicht heruntergeladen werden.");
        }
    };

    // Debug-Funktion
    const runDebug = async () => {
        if (!user) return;

        try {
            const debugFunction = httpsCallable(getFunctions(app), "debugTemplates");
            const result: any = await debugFunction({ userId: user.uid });

            if (result.data?.debugInfo) {
                setDebugInfo(result.data.debugInfo);
                setShowDebug(true);
            }
        } catch (err: any) {
            console.error("Debug-Fehler:", err);
            setError(`Debug fehlgeschlagen: ${err.message}`);
        }
    };

    const b64toBlob = (base64: string, type: string) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        return new Blob([new Uint8Array(byteNumbers)], { type });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white font-sans relative overflow-x-hidden">
            {/* Background Glow */}
            <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-gradient-to-r from-green-500/20 to-blue-500/20 opacity-30 blur-[100px] rounded-full animate-pulse-glow -z-10" />
            <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-gradient-to-r from-purple-500/20 to-pink-500/20 opacity-20 blur-[80px] rounded-full animate-pulse-glow -z-10" />

            {/* HEADER */}
            <div className="bg-white/80 backdrop-blur-lg border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col lg:flex-row lg:items-start gap-6 mb-6">
                        <div className="bg-gradient-to-br from-green-500 to-blue-500 w-16 h-16 rounded-2xl flex items-center justify-center text-white flex-shrink-0">
                            <LayoutTemplate size={32} />
                        </div>
                        <div className="flex-1">
                            <div className="inline-block bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4 border border-green-200 backdrop-blur-sm">
                                🎨 Vorlagen-Bibliothek
                            </div>
                            <h1 className="text-3xl md:text-4xl font-black text-gray-800 mb-3">
                                Eigene <span className="bg-gradient-to-r from-green-500 to-blue-500 bg-clip-text text-transparent">Word-Vorlagen</span> verwalten
                            </h1>
                            <p className="text-gray-600 text-lg">
                                Lade deine eigenen Vorlagen hoch und erstelle KI-generierte Aufgaben im gewünschten Format
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        <span className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium border border-gray-200">
                            <FileText size={14} /> DOCX Format
                        </span>
                        <span className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium border border-gray-200">
                            <Sparkles size={14} /> KI-gestützt
                        </span>
                        <span className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium border border-gray-200">
                            <Settings size={14} /> Platzhalter-Support
                        </span>
                    </div>
                </div>
            </div>

            {/* MAIN CONTENT */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Success/Error Messages */}
                {success && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 animate-fade-in">
                        <Check size={20} className="text-green-600" />
                        <span className="text-green-700 font-medium">{success}</span>
                    </div>
                )}

                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 animate-fade-in">
                        <AlertCircle size={20} className="text-red-600" />
                        <span className="text-red-700 font-medium">{error}</span>
                    </div>
                )}

                {/* Action Bar */}
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                    <button
                        onClick={() => setShowUploadModal(true)}
                        className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-blue-700 hover:-translate-y-1 hover:shadow-xl hover:shadow-green-500/40 transition-all flex items-center justify-center gap-3"
                    >
                        <Upload size={20} />
                        Neue Vorlage hochladen
                    </button>

                    <button
                        onClick={handleDownloadSample}
                        className="bg-gray-100 border border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:border-green-500 hover:text-green-600 hover:-translate-y-1 transition-all flex items-center justify-center gap-3"
                    >
                        <Download size={20} />
                        Beispiel-Vorlage
                    </button>

                    <button
                        onClick={loadTemplates}
                        className="bg-gray-100 border border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:border-yellow-500 hover:text-yellow-600 hover:-translate-y-1 transition-all flex items-center justify-center gap-3"
                    >
                        <RefreshCw size={20} />
                        Neu laden
                    </button>

                    <button
                        onClick={runDebug}
                        className="bg-gray-100 border border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:border-red-500 hover:text-red-600 hover:-translate-y-1 transition-all flex items-center justify-center gap-3"
                    >
                        <Bug size={20} />
                        Debug
                    </button>

                    <Link
                        to="/tasks"
                        className="bg-gray-100 border border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:border-blue-500 hover:text-blue-600 hover:-translate-y-1 transition-all flex items-center justify-center gap-3"
                    >
                        <ExternalLink size={20} />
                        Zum Aufgaben-Generator
                    </Link>
                </div>

                {/* Debug Info */}
                {showDebug && debugInfo && (
                    <div className="mb-6 bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-gray-800">Debug Informationen</h3>
                            <button
                                onClick={() => setShowDebug(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div className="bg-gray-50 p-4 rounded-xl">
                                <div className="text-sm text-gray-500 mb-1">Vorlagen gefunden</div>
                                <div className={`text-3xl font-bold ${debugInfo.templateCount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {debugInfo.templateCount}
                                </div>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-xl">
                                <div className="text-sm text-gray-500 mb-1">User ID</div>
                                <div className="text-sm font-mono text-gray-700 truncate">
                                    {user?.uid}
                                </div>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-xl">
                                <div className="text-sm text-gray-500 mb-1">Status</div>
                                <div className={`text-lg font-bold ${debugInfo.error ? 'text-red-600' : 'text-green-600'}`}>
                                    {debugInfo.error ? 'Fehler' : 'OK'}
                                </div>
                            </div>
                        </div>

                        {debugInfo.templates.length > 0 ? (
                            <div>
                                <h4 className="text-gray-800 font-semibold mb-2">Gefundene Vorlagen:</h4>
                                <div className="space-y-2">
                                    {debugInfo.templates.map((template: any, index: number) => (
                                        <div key={template.id} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <div className="text-gray-800 font-semibold">
                                                        {index + 1}. {template.fileName}
                                                    </div>
                                                    <div className="text-sm text-gray-500">ID: {template.id}</div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {template.exists ? (
                                                        <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                                                            ✓ In Firestore
                                                        </span>
                                                    ) : (
                                                        <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full">
                                                            ✗ Nicht in Firestore
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="text-sm text-gray-600 mt-2">
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div>
                                                        <span className="text-gray-500">User ID Match:</span>
                                                        <span className={`ml-2 ${template.userId === user?.uid ? 'text-green-600' : 'text-red-600'}`}>
                                                            {template.userId === user?.uid ? '✓' : '✗'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Hat TemplateInfo:</span>
                                                        <span className={`ml-2 ${template.data?.templateInfo ? 'text-green-600' : 'text-red-600'}`}>
                                                            {template.data?.templateInfo ? '✓' : '✗'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <p className="text-gray-500">Keine Vorlagen in Firestore gefunden</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Templates Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Templates List */}
                    <div className="lg:col-span-2">
                        <div className="bg-white/80 backdrop-blur-lg border border-gray-200 rounded-2xl p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-gray-800">Deine Vorlagen</h2>
                                <div className="text-sm text-gray-500">
                                    {templates.length} von unbegrenzt
                                </div>
                            </div>

                            {loading ? (
                                <div className="space-y-4">
                                    {[1, 2, 3].map(i => (
                                        <div key={i} className="bg-gray-100 rounded-xl p-5 animate-pulse">
                                            <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
                                            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                        </div>
                                    ))}
                                </div>
                            ) : templates.length === 0 ? (
                                <div className="text-center py-12">
                                    <FolderOpen size={48} className="text-gray-400 mx-auto mb-4" />
                                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Noch keine Vorlagen</h3>
                                    <p className="text-gray-500 mb-6">
                                        Lade deine erste Word-Vorlage hoch, um personalisierte Arbeitsblätter zu erstellen.
                                    </p>
                                    <button
                                        onClick={() => setShowUploadModal(true)}
                                        className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-5 py-2 rounded-xl font-semibold hover:from-green-700 hover:to-blue-700 transition-all flex items-center gap-2 mx-auto"
                                    >
                                        <Upload size={18} />
                                        Erste Vorlage hochladen
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {templates.map(template => (
                                        <div
                                            key={template.id}
                                            className="bg-gray-50/80 border border-gray-200 rounded-xl p-5 hover:border-green-500/50 hover:shadow-md transition-all group"
                                        >
                                            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                                                {/* Icon */}
                                                <div className="bg-gradient-to-br from-green-500/20 to-blue-500/20 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                                                    <FileText size={24} className="text-green-600" />
                                                </div>

                                                {/* Info */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                                        <div>
                                                            <h3 className="text-lg font-bold text-gray-800 truncate">
                                                                {template.fileName}
                                                            </h3>
                                                            <div className="flex flex-wrap gap-2 mt-2">
                                                                {template.templateInfo.hasTasksPlaceholder && (
                                                                    <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                                                                        <Check size={10} />
                                                                        Aufgaben-Platzhalter
                                                                    </span>
                                                                )}
                                                                {template.templateInfo.hasTitlePlaceholder && (
                                                                    <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                                                                        <Check size={10} />
                                                                        Titel-Platzhalter
                                                                    </span>
                                                                )}
                                                                {template.templateInfo.placeholderCount > 0 && (
                                                                    <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">
                                                                        {template.templateInfo.placeholderCount} Platzhalter
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="text-sm text-gray-500">
                                                            {template.createdAt && new Date(template.createdAt).toLocaleDateString('de-DE')}
                                                        </div>
                                                    </div>

                                                    {template.templateInfo.sections.length > 0 && (
                                                        <div className="mt-4">
                                                            <div className="text-xs text-gray-500 font-semibold mb-1">Inhalt:</div>
                                                            <p className="text-sm text-gray-600 line-clamp-2">
                                                                {template.templateInfo.sections[0]}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex flex-wrap gap-3 mt-6 pt-5 border-t border-gray-200">
                                                <Link
                                                    to={`/worksheets/create/${template.id}`}
                                                    className="flex-1 min-w-[140px] bg-green-100 text-green-700 border border-green-200 hover:bg-green-200 py-2 px-4 rounded-xl text-center font-semibold transition-all flex items-center justify-center gap-2"
                                                >
                                                    <Edit size={16} />
                                                    Mit dieser Vorlage arbeiten
                                                </Link>

                                                <button
                                                    onClick={() => handleDeleteTemplate(template.id, template.fileName)}
                                                    className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 rounded-xl font-semibold transition-all flex items-center gap-2"
                                                >
                                                    <Trash2 size={16} />
                                                    Löschen
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Info Sidebar */}
                    <div className="space-y-6">
                        {/* How To Card */}
                        <div className="bg-gradient-to-br from-green-500/10 to-blue-500/10 border border-green-500/20 rounded-2xl p-6">
                            <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-3">
                                <Info size={18} className="text-green-600" />
                                So funktioniert's
                            </h4>
                            <ul className="space-y-3 text-gray-600">
                                <li className="flex items-start gap-2">
                                    <span className="text-green-600">1.</span>
                                    <span className="text-sm">Lade eine Word-Vorlage (.docx) hoch</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-600">2.</span>
                                    <span className="text-sm">Verwende Platzhalter wie <code className="bg-gray-100 px-1 rounded text-gray-800">{"{{tasks}}"}</code></span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-600">3.</span>
                                    <span className="text-sm">Wähle die Vorlage im Aufgaben-Generator</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-600">4.</span>
                                    <span className="text-sm">KI erstellt Aufgaben im Vorlagen-Format</span>
                                </li>
                            </ul>
                        </div>

                        {/* Placeholders Card */}
                        <div className="bg-white/80 backdrop-blur-lg border border-gray-200 rounded-2xl p-6 shadow-sm">
                            <h4 className="text-lg font-bold text-gray-800 mb-4">Unterstützte Platzhalter</h4>
                            <div className="space-y-3">
                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <code className="text-green-600 font-mono text-sm">{"{{tasks}}"}</code>
                                    <p className="text-sm text-gray-500 mt-1">Wird durch KI-generierte Aufgaben ersetzt</p>
                                </div>
                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <code className="text-green-600 font-mono text-sm">{"{{title}}"}</code>
                                    <p className="text-sm text-gray-500 mt-1">Wird durch das Unterrichtsthema ersetzt</p>
                                </div>
                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <code className="text-green-600 font-mono text-sm">{"{{date}}"}</code>
                                    <p className="text-sm text-gray-500 mt-1">Wird durch das aktuelle Datum ersetzt</p>
                                </div>
                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <code className="text-green-600 font-mono text-sm">{"{{name}}"}</code>
                                    <p className="text-sm text-gray-500 mt-1">Für Schüler-Namen (manuelle Eingabe)</p>
                                </div>
                            </div>
                        </div>

                        {/* Tips Card */}
                        <div className="bg-white/80 backdrop-blur-lg border border-gray-200 rounded-2xl p-6 shadow-sm">
                            <h4 className="text-lg font-bold text-gray-800 mb-4">📝 Tipps für Vorlagen</h4>
                            <ul className="space-y-2 text-sm text-gray-600">
                                <li className="flex items-start gap-2">
                                    <span className="text-green-600">•</span>
                                    <span>Verwende klare Platzhalter im Format <code className="bg-gray-100 px-1 rounded text-gray-800">{"{{platzhalter}}"}</code></span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-600">•</span>
                                    <span>Speichere als .docx (Word 2007+)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-600">•</span>
                                    <span>Maximale Dateigröße: 5MB</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-green-600">•</span>
                                    <span>Formatierung bleibt erhalten</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Upload Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white border border-gray-200 rounded-2xl p-6 max-w-md w-full animate-fade-in shadow-xl">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-gray-800">Vorlage hochladen</h3>
                            <button
                                onClick={() => {
                                    setShowUploadModal(false);
                                    setSelectedFile(null);
                                    setError("");
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Upload Area */}
                            <div
                                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                                    selectedFile
                                        ? 'border-green-500 bg-green-50'
                                        : 'border-gray-300 hover:border-green-500 hover:bg-gray-50'
                                }`}
                                onClick={() => document.getElementById('template-upload')?.click()}
                            >
                                <input
                                    id="template-upload"
                                    type="file"
                                    accept=".docx"
                                    className="hidden"
                                    onChange={handleFileSelect}
                                />

                                {selectedFile ? (
                                    <div className="space-y-3">
                                        <FileText size={48} className="text-green-600 mx-auto" />
                                        <div>
                                            <div className="text-gray-800 font-semibold">{selectedFile.name}</div>
                                            <div className="text-sm text-gray-500">
                                                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedFile(null);
                                            }}
                                            className="text-sm text-gray-500 hover:text-gray-700"
                                        >
                                            Andere Datei wählen
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="w-16 h-16 border-2 border-gray-300 rounded-xl flex items-center justify-center mx-auto">
                                            <Upload size={24} className="text-gray-400" />
                                        </div>
                                        <div>
                                            <div className="text-gray-800 font-semibold">Word-Vorlage hochladen</div>
                                            <div className="text-sm text-gray-500 mt-1">
                                                Klicke zum Auswählen oder ziehe eine .docx-Datei hierhin
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-400">
                                            Max. 5MB, nur .docx
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Progress Bar */}
                            {uploading && (
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Wird hochgeladen...</span>
                                        <span className="text-green-600">{uploadProgress}%</span>
                                    </div>
                                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-green-500 to-blue-500 transition-all duration-300"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setShowUploadModal(false);
                                        setSelectedFile(null);
                                        setError("");
                                    }}
                                    className="flex-1 py-3 px-4 border-2 border-gray-300 rounded-xl text-gray-600 font-semibold hover:border-gray-400 hover:text-gray-800 transition-all"
                                    disabled={uploading}
                                >
                                    Abbrechen
                                </button>
                                <button
                                    onClick={handleUpload}
                                    disabled={!selectedFile || uploading}
                                    className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all ${
                                        !selectedFile || uploading
                                            ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400'
                                            : 'bg-gradient-to-r from-green-600 to-blue-600 text-white hover:from-green-700 hover:to-blue-700'
                                    }`}
                                >
                                    {uploading ? 'Wird hochgeladen...' : 'Hochladen'}
                                </button>
                            </div>

                            {/* Info */}
                            <div className="text-xs text-gray-500 p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <Info size={14} className="flex-shrink-0 mt-0.5" />
                                    <span>Die Vorlage wird automatisch analysiert. Platzhalter werden erkannt.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}